"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.readingScoreSchema = exports.scoredRowSchema = exports.scoredValueSchema = exports.readingScoreInputSchema = exports.scoreRowInputSchema = void 0;
// Scoring a DRAFT: what the nurse is typing, graded before anything is saved (`RPM-008`, `J-470`).
//
// WHY A ROUTE AND NOT A CLIENT FUNCTION. `RPM-008`'s API row is explicit: "Detection runs server-side
// in the classification service; the client never decides a type. The workup contract returns the
// detected type and its route so the surface can show both." The bands are the same: `RPM-011` says
// "The client renders bands; it never computes them." A browser that decided either would be a second
// copy of the truth, and the one that drifts is the one on the screen.
//
// IT WRITES NOTHING, AND THAT IS THE POINT. A draft is not a reading. `RPM-005`'s idempotency, the
// audit row, the workup and the notification obligation all belong to the SAVE (`RPM-100`); a route
// that scored by writing a row and deleting it would put a clinical value in the record every time
// she paused typing.
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
const rpm_entries_js_1 = require("./rpm-entries.js");
/**
 * What the picker can send: a single-row vital, or `BP` — the picker offers blood pressure as ONE
 * option, and the save carries it as the `bloodPressure` pair. Accepting only the four single-row
 * codes refused every hand-picked blood pressure with `400` (`J-506`).
 */
const SCORE_PICKS = [...rpm_entries_js_1.SINGLE_ROW_VITALS, "BP"];
exports.scoreRowInputSchema = zod_1.z.strictObject({
    /** As typed, exactly. Detection reads this and echoes it back verbatim when it cannot resolve it. */
    text: zod_1.z.string().max(200),
    /** Her pick, when she made one. Absent means detection decides (`NURSE_PICKED` beats every route). */
    vitalCode: zod_1.z.enum(SCORE_PICKS).optional(),
    /** As typed, when she changed it. Absent means the definition's canonical code. */
    unit: zod_1.z.string().min(1).max(40).optional(),
    /** Required to score an oxygen saturation, because `cb_oxygen_scope` scopes SpO₂'s bands to it. */
    oxygenStatus: zod_1.z.enum(["ROOM_AIR", "ON_OXYGEN", "UNKNOWN"]).optional(),
});
exports.readingScoreInputSchema = zod_1.z
    .strictObject({
    /**
     * The rows as they stand. No submission key, no building, no resident.
     *
     * NONE OF THOSE THREE IS HERE AND THAT IS DELIBERATE. A draft belongs to nobody yet: sending a
     * resident id would put PHI in a request that writes nothing and is made on every keystroke, and
     * the bands this route reads are tenant-owned rather than per-building. What comes back says what
     * the text is and what it would score; what it is ABOUT is settled at the save.
     */
    rows: zod_1.z.array(exports.scoreRowInputSchema).min(1).max(20),
    /**
     * The entry the rows are typed on, when it is already stored (`RPM-059` row 4, `D-1388`). An
     * open entry keeps the band set it pinned, and its save scores against that set, so the preview
     * does too. Absent for a new entry: the current set decides, as its first save does. An opaque
     * id of the entry, never who it is about.
     */
    rpmAlertId: zod_1.z.uuid().optional(),
})
    .meta({ description: "One capture surface as it stands, scored and not stored" });
/** One value the row resolved to. A blood-pressure row resolves to two. */
exports.scoredValueSchema = zod_1.z.strictObject({
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    valueNumeric: zod_1.z.string().nullable(),
    valueUnit: zod_1.z.string().nullable(),
    unitResolution: zod_1.z.enum(rpm_enums_js_1.unitResolution.enumValues),
    plausibilityStatus: zod_1.z.enum(["PLAUSIBLE", "IMPLAUSIBLE"]),
    severityBand: zod_1.z.enum(rpm_enums_js_1.severityBand.enumValues).nullable(),
    /** The question SpO₂ asks when its band depends on an answer she has not given (`RPM-012`). */
    severityReason: zod_1.z.string().nullable(),
});
exports.scoredRowSchema = zod_1.z.strictObject({
    rowPosition: zod_1.z.number().int(),
    /** One code, the two blood-pressure halves, or none while the row is `AMBIGUOUS`. */
    vitalCodes: zod_1.z.array(zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues)),
    /** Which of the four routes decided it (`RPM-008`'s precedence). */
    vitalTypeResolution: zod_1.z.enum(rpm_enums_js_1.vitalTypeResolution.enumValues),
    /** The explicit word that named the vital, as captured. Null on every other route. */
    matchedWord: zod_1.z.string().nullable(),
    values: zod_1.z.array(exports.scoredValueSchema),
    /** True while this row's type is `AMBIGUOUS`, which is when its picker is shown. */
    needsPick: zod_1.z.boolean(),
    /**
     * Why nothing was taken from the text, in the extractor's own words, echoing what she typed —
     * `No number found in "temp"`, or `"HR 70-130" is a range — enter the one value that was taken`
     * (`RPM-009`, `J-427`). Null when a number was taken or the row is still `AMBIGUOUS`.
     */
    blockedMessage: zod_1.z.string().nullable(),
});
exports.readingScoreSchema = zod_1.z
    .strictObject({
    /** The set every band here came from. The surface says WHICH version it is showing (`RPM-011`). */
    classificationBandSetId: zod_1.z.uuid(),
    rows: zod_1.z.array(exports.scoredRowSchema),
    /**
     * The one announcement when any row is `AMBIGUOUS`, naming the rows and echoing each text
     * verbatim (`RPM-008`'s Blocking row). Null when every row resolved.
     */
    blockedMessage: zod_1.z.string().nullable(),
})
    .meta({ id: "ReadingScore", description: "What the draft is, and what it would score" });
//# sourceMappingURL=rpm-scoring.js.map